import React, { RefObject, FC, useEffect } from "react";
import { useSelector } from "react-redux";
import UpcomingRaces from "@urp/upcoming-races";
import {
  getRaceTracksIsSearchActive,
  getRaceTracksSearchQuery,
  getRaceTypesFilter,
  getRegionsFilter
} from "@urp/lib-racetracks/src/redux/selectors";
import {
  RESULTED_RACES_STATUS,
  TABS,
  useRaceTracks
} from "@urp/lib-racetracks";
import { getUpcomingRacesInfo } from "@urp/store-selectors";
import { TabContentContainer } from "./styled-components";
import RaceTracksSearch from "../../components/RaceTracksSearch";
import RaceTracksFilter from "../../components/RaceTracksFilter";
import FavoriteTracks from "../../components/FavoriteTracks";
import TracksAZ from "../../components/TracksAZ";
import EmptyMessage from "../../components/EmptyMessage";
import TracksInformationList from "../../components/TracksInformationList";

interface TabContentProps {
  currentTab: TABS;
  seoAreaRef?: RefObject<HTMLDivElement>;
}

const TabContent: FC<TabContentProps> = ({ currentTab, seoAreaRef }) => {
  const isTracksSearchActive = useSelector(getRaceTracksIsSearchActive);
  const regionsFilter = useSelector(getRegionsFilter);
  const raceTypesFilter = useSelector(getRaceTypesFilter);
  const tracksSearchQuery = useSelector(getRaceTracksSearchQuery);
  const {
    title: upcomingRacesTitle,
    noUpcomingRaces: noUpcomingRacesMessages
  } = useSelector(getUpcomingRacesInfo);

  const isRaceTypesFilterEnabled = currentTab !== TABS.ALL_TRACKS;
  const filterOptions = {
    isTracksSearchActive,
    isRaceTypesFilterEnabled,
    regionsFilter: isTracksSearchActive ? [] : regionsFilter,
    raceTypesFilter:
      !isRaceTypesFilterEnabled || isTracksSearchActive ? [] : raceTypesFilter,
    tracksSearchQuery
  };

  const { tracksRaces, processingTracksRaces } = useRaceTracks({
    ...filterOptions,
    raceStatusFilter: currentTab === TABS.RESULTS ? RESULTED_RACES_STATUS : []
  });

  useEffect(() => {
    const seoAreaElement = seoAreaRef?.current;
    if (seoAreaElement) {
      seoAreaElement.hidden = processingTracksRaces;
    }
  }, [processingTracksRaces]);

  const renderContentHeader = () => (
    <div>
      <RaceTracksSearch analyticsEnrichment={{ activeTab: currentTab }} />
      {!filterOptions.isTracksSearchActive && (
        <RaceTracksFilter
          currentTab={currentTab}
          paddingTop={0}
          isRaceTypesFilterEnabled={filterOptions.isRaceTypesFilterEnabled}
        />
      )}
    </div>
  );

  const renderContentBody = () => {
    switch (currentTab) {
      case TABS.TODAY:
        return (
          <TabContentContainer>
            {tracksRaces?.length || processingTracksRaces ? (
              <>
                <UpcomingRaces
                  filterOptions={filterOptions}
                  title={upcomingRacesTitle}
                  noUpcomingRacesMessages={noUpcomingRacesMessages}
                />
                <FavoriteTracks filterOptions={filterOptions} />
                <TracksAZ filterOptions={filterOptions} />
              </>
            ) : (
              <EmptyMessage
                currentTab={TABS.TODAY}
                isGlobal
                isTracksSearchActive={filterOptions.isTracksSearchActive}
              />
            )}
          </TabContentContainer>
        );
      case TABS.RESULTS:
        return (
          <TabContentContainer>
            {tracksRaces?.length || processingTracksRaces ? (
              <>
                <FavoriteTracks filterOptions={filterOptions} isResults />
                <TracksAZ filterOptions={filterOptions} isResults />
              </>
            ) : (
              <EmptyMessage
                currentTab={TABS.RESULTS}
                isGlobal
                isTracksSearchActive={filterOptions.isTracksSearchActive}
              />
            )}
          </TabContentContainer>
        );
      case TABS.ALL_TRACKS:
        return (
          <TabContentContainer>
            <TracksInformationList showSeoFooter />
          </TabContentContainer>
        );

      default:
        return <></>;
    }
  };

  return (
    <TabContentContainer>
      {renderContentHeader()}
      {renderContentBody()}
    </TabContentContainer>
  );
};

export default TabContent;
